/**
 * useCampaignQueries — shared campaign-detail data hook.
 *
 * Reads campaign + every roster tab via the unified /api/v1/* surface.
 * No role parameter, no role-prefixed URLs — the BE projects per-role
 * fields server-side based on the JWT. Same call shape everywhere.
 *
 * Pagination contract: every roster tab is paginated. This hook pulls
 * a generous page (`limit: 200`) so the legacy "give me everything"
 * downstream consumers (reporting math, avatar collectors, brief
 * auto-mentions) keep working without a separate cursor flow. Campaigns
 * exceeding 200 entries per tab need to migrate to the per-tab hooks
 * (useCreatorsTab / useScriptTab / etc.) which expose proper pagination.
 */
import { useQueries } from '@tanstack/react-query';
import {
  campaignKeys,
  createCampaignDetailApi,
} from '@discovr/features/campaign-detail';

// Singleton — no role state, no per-app args. Built once per module load.
const api = createCampaignDetailApi();

// `limit: 200` covers every realistic campaign size today. Past 200,
// downstream "give me all entries" consumers need to migrate to the
// per-tab hooks (useCreatorsTab / useScriptTab / etc.) which expose
// proper cursor pagination. Tracked under the React-Query 4-states
// refactor that supersedes this hook.
const FULL_PAGE = { page: 1, limit: 200 } as const;

export interface UseCampaignQueriesOptions {
  /** Fetch the six full roster lists. Defaults to true for back-compat; the
   *  brand page passes `false`. Tab count badges come from `/roster/counts`,
   *  each tab paginates its own page, and Reporting → Creator Content reads its
   *  own report, so these lists are not part of the brand campaign-open path. */
  rosterEnabled?: boolean;
}

export function useCampaignQueries(
  id: string,
  { rosterEnabled = true }: UseCampaignQueriesOptions = {},
) {
  const ready = !!id;
  // Campaign detail stays unconditional (it backs the page shell); the roster
  // lists are opt-in.
  const rosterReady = ready && rosterEnabled;
  const [campaign, creators, rejected, scripts, content, finalized, live] = useQueries({
    queries: [
      {
        queryKey: campaignKeys.detail(id),
        queryFn: () => api.getCampaign(id),
        enabled: ready,
        staleTime: 30_000,
      },
      {
        queryKey: campaignKeys.rosterTab(id, 'creators'),
        queryFn: () => api.listCreators(id, FULL_PAGE),
        enabled: rosterReady,
        staleTime: 30_000,
      },
      {
        queryKey: campaignKeys.rosterTab(id, 'rejected'),
        queryFn: () => api.listRejected(id, FULL_PAGE),
        enabled: rosterReady,
        staleTime: 30_000,
      },
      {
        queryKey: campaignKeys.rosterTab(id, 'scripts'),
        queryFn: () => api.listScript(id, FULL_PAGE),
        enabled: rosterReady,
        staleTime: 30_000,
      },
      {
        queryKey: campaignKeys.rosterTab(id, 'content'),
        queryFn: () => api.listContent(id, FULL_PAGE),
        enabled: rosterReady,
        staleTime: 30_000,
      },
      {
        queryKey: campaignKeys.rosterTab(id, 'finalized'),
        queryFn: () => api.listFinalized(id, FULL_PAGE),
        enabled: rosterReady,
        staleTime: 30_000,
      },
      {
        queryKey: campaignKeys.rosterTab(id, 'live-content'),
        queryFn: () => api.listLiveContent(id, FULL_PAGE),
        enabled: rosterReady,
        staleTime: 30_000,
      },
    ],
  });

  return { campaign, creators, rejected, scripts, content, finalized, liveContent: live };
}
